import { DateTime } from 'luxon';
import { timezones } from '@/constants/localizer';

/**
 * A leitura é feita em UTC de ponta a ponta, e o fuso vai explícito em vez de
 * herdado do `Settings.defaultZone` que o `datetimeService` configura: aqui o
 * resultado é uma diferença entre dois instantes, e diferença não muda com o fuso
 * de quem lê. Fixar evita que a contagem dependa de qual módulo carregou primeiro.
 */
const OPTIONS = { zone: timezones.UTC };

/**
 * Unidades da mais grossa para a mais fina. A ordem é o algoritmo: a leitura
 * começa na primeira unidade com valor e desce a partir dela.
 */
export const ELAPSED_UNITS = [
  'years',
  'months',
  'days',
  'hours',
  'minutes',
  'seconds',
];

/**
 * Quantas unidades a leitura mostra.
 *
 * Três é o limite porque a terceira já é ruído: em `2m 20d 10h`, a hora não muda
 * decisão nenhuma — mas cortar em duas esconderia os vinte dias, que mudam. Abaixo
 * de um dia sobram duas unidades e a leitura fica naturalmente curta.
 */
export const MAX_ELAPSED_PARTS = 3;

/**
 * Decompõe o tempo decorrido desde um instante em unidades de calendário.
 *
 * Existe porque hora sozinha mente por acúmulo: `9840h` é um número que ninguém
 * lê, e o operador precisa saber se o alarme é de hoje de manhã ou de treze meses
 * atrás. A contagem passa pelo Luxon, e não por divisão de milissegundos, porque
 * mês e ano não têm duração fixa — fevereiro e janeiro não podem virar o mesmo
 * "1m".
 *
 * Data sem fuso é lida como UTC, que é a convenção da API: ver
 * `Settings.defaultZone` em `datetimeService.js`.
 *
 * @param {String} startISO Instante do evento, em ISO.
 * @param {Number} nowInMs Relógio da página, para a leitura não congelar.
 * @returns {Object[]|null} Até três `{ unit, value }`, ou nulo se a data não serve.
 */
export default function describeElapsed(startISO, nowInMs) {
  if (!startISO) {
    return null;
  }

  const start = DateTime.fromISO(startISO, OPTIONS);
  if (!start.isValid) {
    return null;
  }

  const end = DateTime.fromMillis(nowInMs, OPTIONS);
  if (!end.isValid || end <= start) {
    // Evento no futuro é relógio dessincronizado, não viagem no tempo: a leitura
    // vira "agora" em vez de um negativo que assusta sem informar.
    return [{ unit: 'seconds', value: 0 }];
  }

  const diff = end.diff(start, ELAPSED_UNITS).toObject();
  const parts = ELAPSED_UNITS
    .map((unit) => ({ unit, value: Math.floor(diff[unit] || 0) }));

  const first = parts.findIndex((part) => part.value > 0);
  if (first < 0) {
    return [{ unit: 'seconds', value: 0 }];
  }

  const slice = parts.slice(first, first + MAX_ELAPSED_PARTS);

  // Zero à direita é ruído: `1d 0h 0min` diz menos que `1d`.
  while (slice.length > 1 && slice[slice.length - 1].value === 0) {
    slice.pop();
  }

  return slice;
}
